import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { useAttribution } from '../contexts/AttributionContext';
import {
  syncCredentialsToExtension,
  stopReactiveSyncForUser,
} from '../services/extension-credential-sync';

const DEBUG = import.meta.env.DEV;
const PRIVACY_TOGGLES_KEY = 'ownyou_privacy_toggles';

/**
 * Slice-1 reconciliation: install-consent is the authoritative gate.
 *
 * `isBbsSharingEnabled` (in `services/extension-credential-sync.ts`) reads
 * the legacy Sprint-14-era `ownyou_privacy_toggles.bbs` localStorage flag.
 * The Slice-1 install-consent flow records consent under
 * `attributionConsent::install` in the extension's chrome.storage.local
 * but never sets the legacy flag — so credential-sync stalls with
 * `state: 'disabled'` and the wallet never receives the claimSet /
 * bbsSignature / disclosurePolicy / preferences it needs. Result: the
 * publisher SDK's `handleRequestDisclosure` throws "No claim set
 * available", the bid request fires without `user.ext.ownyou`, the DSP
 * returns no-bid, ad slots stall on "LOADING ADVERT...".
 *
 * Fix: once install-consent is recorded, mirror it into the legacy flag
 * so `isBbsSharingEnabled` returns true and `syncCredentialsToExtension`
 * proceeds. Idempotent — only writes when the flag is missing or stale.
 * The proper architectural fix (delete `isBbsSharingEnabled`, gate on
 * install-consent everywhere) belongs to the post-walk remediation pass.
 */
function reconcilePrivacyTogglesFromInstallConsent(): void {
  try {
    const raw = localStorage.getItem(PRIVACY_TOGGLES_KEY);
    const current = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    if (current.bbs === true) return;
    localStorage.setItem(
      PRIVACY_TOGGLES_KEY,
      JSON.stringify({
        ...current,
        bbs: true,
        demographics: true,
        household: true,
        iab: true,
        purchase_intent: true,
      }),
    );
    if (DEBUG) {
      console.log('[ExtensionCredentialSync] Reconciled ownyou_privacy_toggles from install-consent');
    }
  } catch {
    // localStorage may be JSON-corrupt; overwrite.
    localStorage.setItem(
      PRIVACY_TOGGLES_KEY,
      JSON.stringify({
        bbs: true,
        demographics: true,
        household: true,
        iab: true,
        purchase_intent: true,
      }),
    );
  }
}

export function useExtensionCredentialSync(): void {
  const { wallet, isAuthenticated, signer } = useAuth();
  const { store, isReady } = useStore();
  const { installConsentStatus } = useAttribution();

  useEffect(() => {
    // Sprint 18c remediation Phase 1 (G2): walletSigner is required so the
    // PWA can derive nymSecret + bbsKeyPair via @ownyou/identity. Without a
    // signer, the extension would have to fabricate identity material — the
    // exact bug G2 closes.
    if (!isAuthenticated || !wallet || !store || !isReady || !signer) return;

    // Reconcile the legacy privacy-toggles flag from install-consent state
    // before kicking off sync. Without this, install-consent users who
    // never touched the Sprint-14 privacy-toggles UI would stall here.
    if (installConsentStatus === 'recorded') {
      reconcilePrivacyTogglesFromInstallConsent();
    }

    let cancelled = false;

    syncCredentialsToExtension({ store, userId: wallet.address, walletSigner: signer })
      .then((status) => {
        if (cancelled) return;
        // Slice-1: surface non-success states in production so HITL walks
        // don't have to dig through DEV-gated logs. `synced` stays silent;
        // anything else logs so the page console shows what's wrong.
        if (status.state === 'synced') {
          if (DEBUG) console.log('[ExtensionCredentialSync] Synced:', status);
        } else if (status.state === 'unavailable') {
          console.warn('[ExtensionCredentialSync] Extension API unavailable — install/enable the OwnYou extension to participate in disclosure.');
        } else {
          console.error('[ExtensionCredentialSync] Sync did not complete:', status);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[ExtensionCredentialSync] Sync threw:', err);
        }
      });

    // Eighth-pass MEDIUM: tear down the reactive sync subscription bound
    // to (store, wallet.address) on unmount or dep-change. Pre-fix the
    // hook only flipped `cancelled`; the reactive listeners installed by
    // `syncCredentialsToExtension` → `setupReactiveResync` survived
    // account / store transitions and held stale references.
    const userIdAtMount = wallet.address;
    const storeAtMount = store;
    return () => {
      cancelled = true;
      stopReactiveSyncForUser(storeAtMount, userIdAtMount);
    };
  }, [isAuthenticated, wallet, store, isReady, signer, installConsentStatus]);
}
